<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';

import { useMapGetter } from 'dashboard/composables/store';
import { useVisitasStore } from 'dashboard/stores/visitas';
import { frontendURL } from 'dashboard/helper/URLHelper';
import ImovelAPI from 'dashboard/api/imoveis';
import {
  BUSINESS_CONTEXTS,
  primaryBusinessContext,
} from 'dashboard/constants/lais/businessContext';
import LaisActionBar from 'dashboard/components-next/LaisActionBar.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisInfoRow from 'dashboard/components-next/LaisInfoRow.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  formatVisitDateTime,
  visitFinishTitle,
  visitSummaryRows,
} from '../utils/visitSuccessUtils';
import {
  buildPostVisitReportPayload,
  createPostVisitArtifactRevisionId,
} from '../utils/postVisitReport';
import {
  formatReferenceFollowUpPayload,
  formatReferenceFollowUpV3Payload,
  formatReferenceMissingInfoPayload,
} from '../utils/referenceVisitLogic';
import {
  CORRECT_ENTRY_INFO_OPTIONS,
  ENTRANCE_BLOCKER_OPTIONS,
  ENTRY_DOORMAN_OPTIONS,
  KEY_PROBLEM_OPTIONS,
} from '../utils/visitFlowCopy';
import VisitFinishSkeleton from '../components/VisitFinishSkeleton.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const store = useVisitasStore();

const isLoading = ref(true);
const isSaving = ref(false);
const isFinished = ref(false);
const saveError = ref('');
const reportRevisionId = ref(createPostVisitArtifactRevisionId());
const visit = ref({});
const stepIndex = ref(0);
const outcome = computed(() => String(route.query.outcome || 'done'));
const input =
  'w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none';
const YES_NO_OPTIONS = Object.freeze([
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
]);
const VISIT_TYPE_OPTIONS = Object.freeze([
  { value: 'presencial', label: 'Presencial' },
  { value: 'video', label: 'Por vídeo chamada' },
  { value: 'message', label: 'Mensagem' },
]);
const PROPERTY_VALUE_OPTIONS = Object.freeze([
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
  { value: 'unknown', label: 'Não sei' },
]);
const PROPERTY_OPTIONS_BY_FIELD = Object.freeze({
  has_doorman: ENTRY_DOORMAN_OPTIONS,
});
const PROPERTY_FIELDS = Object.freeze([
  { key: 'has_balcony', label: 'O imóvel possui varanda?' },
  { key: 'has_gym', label: 'Tem academia?' },
  { key: 'has_elevator', label: 'O prédio tem elevador?' },
  { key: 'has_pool', label: 'Possui piscina no condomínio?' },
  { key: 'has_doorman', label: 'Tem portaria?' },
]);
const ENTRANCE_PROBLEM_BY_BLOCKER = Object.freeze({
  wrong_info: 'wrong_address',
  key_problem: 'keys_not_available',
  no_permission: 'no_entrance_permission',
  out_of_hours: 'visit_off_allowed_hours',
  tenant_denied: 'resident_tenant_didnt_allow_visit',
  no_answer: 'landlord_no_show',
  address_not_found: 'wrong_address',
  construction_or_renovation: 'temporarily_unavailable',
  safety_or_pet: 'no_entrance_permission',
  condominium_denied: 'no_collective_authorization',
  other: 'other',
});
const ENTRANCE_PROBLEM_BY_KEY_PROBLEM = Object.freeze({
  key_does_not_open: 'missplaced_keys',
  lock_changed: 'missplaced_keys',
  key_not_available: 'keys_not_available',
  password_or_biometry_failed: 'wrong_password',
  damaged_key: 'damaged_keys',
});
const BASE_STEPS = Object.freeze([
  { key: 'presence', label: 'Quem compareceu?' },
  { key: 'entrance', label: 'Como foi sua entrada no imóvel?' },
  { key: 'property', label: 'Ajude a completar o anúncio' },
]);

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const normalizeToken = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const booleanFrom = value => {
  if (typeof value === 'boolean') return value;
  const normalized = normalizeToken(value);
  if (['1', 'true', 'yes', 'sim'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'nao', 'não'].includes(normalized)) return false;
  return null;
};

const radioOptionClass = (currentValue, optionValue) =>
  currentValue === optionValue
    ? 'border-lais-primary bg-lais-primary-50 text-lais-primary shadow-sm'
    : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary/60';

const propertyOptionsFor = field =>
  PROPERTY_OPTIONS_BY_FIELD[field.key] || PROPERTY_VALUE_OPTIONS;

const form = reactive({
  agent_present: outcome.value === 'not_done' ? '' : 'yes',
  visitor_present: ['not_done', 'no_show'].includes(outcome.value)
    ? 'no'
    : 'yes',
  owner_present: '',
  visit_type: 'presencial',
  did_enter: ['not_done', 'no_show'].includes(outcome.value) ? 'no' : 'yes',
  entry_info_confirmed: '',
  entrance_confirmation: '',
  entrance_blocker: outcome.value === 'no_show' ? 'no_answer' : '',
  correct_entry_info: '',
  key_problem: '',
  comment: '',
  has_balcony: '',
  has_gym: '',
  has_elevator: '',
  has_pool: '',
  has_doorman: '',
  owner_feedback: '',
});

const summaryRows = computed(() => visitSummaryRows(visit.value));
const visitedImovel = computed(() => visit.value.imovel || {});
const visitAttrs = computed(
  () =>
    visit.value.additionalAttributes || visit.value.additional_attributes || {}
);
const visitedImovelAttrs = computed(
  () =>
    visitedImovel.value.additionalAttributes ||
    visitedImovel.value.additional_attributes ||
    {}
);
const entryAccessDetails = computed(
  () =>
    visitAttrs.value.entryAccess ||
    visitAttrs.value.entry_access ||
    visitedImovel.value.entryAccess ||
    visitedImovel.value.entry_access ||
    visitedImovelAttrs.value.entryAccess ||
    visitedImovelAttrs.value.entry_access ||
    {}
);
const entryAccessType = computed(() =>
  firstPresent(
    entryAccessDetails.value.accessKey,
    entryAccessDetails.value.access_key,
    entryAccessDetails.value.entryAccessType,
    entryAccessDetails.value.entry_access_type,
    entryAccessDetails.value.kind,
    entryAccessDetails.value.type,
    entryAccessDetails.value.keyHolder,
    entryAccessDetails.value.key_holder,
    visitAttrs.value.entryAuthorization,
    visitAttrs.value.entry_authorization,
    visitAttrs.value.entradaNoImovel,
    visitAttrs.value.entrada_no_imovel,
    visitedImovelAttrs.value.entryAccessType,
    visitedImovelAttrs.value.entry_access_type
  )
);
const agentHasKeys = computed(() => {
  const explicit = booleanFrom(
    firstPresent(
      entryAccessDetails.value.keysWithAgent,
      entryAccessDetails.value.keys_with_agent,
      entryAccessDetails.value.agentHasKeys,
      entryAccessDetails.value.agent_has_keys,
      visitAttrs.value.agentHasKeys,
      visitAttrs.value.agent_has_keys
    )
  );

  if (explicit !== null) return explicit;

  const accessType = normalizeToken(entryAccessType.value);
  return [
    'keys_with_agent',
    'keyswithagent',
    'agent_keys',
    'broker_keys',
    'corretor',
  ].some(token => accessType.includes(token));
});
const requiresOwnerPresent = computed(() => {
  if (agentHasKeys.value) return false;

  const explicit = booleanFrom(
    firstPresent(
      entryAccessDetails.value.requiresOwnerPresent,
      entryAccessDetails.value.requires_owner_present,
      entryAccessDetails.value.ownerPresentRequired,
      entryAccessDetails.value.owner_present_required
    )
  );

  if (explicit !== null) return explicit;

  const accessType = normalizeToken(entryAccessType.value);
  return [
    'owner_present',
    'keys_with_owner',
    'keyswithowner',
    'owner',
    'proprietario',
    'proprietário',
  ].some(token => accessType.includes(token));
});
const shouldAskOwnerPresent = computed(
  () => !agentHasKeys.value && requiresOwnerPresent.value
);
const visitedHouseLink = computed(
  () =>
    visitedImovel.value.linkExterno ||
    visitedImovel.value.link_externo ||
    visitedImovel.value.publicUrl ||
    visitedImovel.value.public_url ||
    ''
);
const finalStatus = computed(() => {
  if (form.agent_present === 'no' || form.visitor_present === 'no') {
    return 'nao_compareceu';
  }
  if (form.did_enter === 'no') return 'nao_sucedida';
  return 'realizada';
});
const businessContext = computed(() =>
  primaryBusinessContext([
    visit.value.businessContext,
    visit.value.business_context,
    visitedImovel.value.businessContext,
    visitedImovel.value.business_context,
    visitedImovel.value.finalidade,
    visitedImovel.value.transactionType,
    visitedImovel.value.transaction_type,
  ])
);
const isForSale = computed(
  () => businessContext.value === BUSINESS_CONTEXTS.FOR_SALE
);
const anyOwnerPresent = computed(() => {
  if (form.owner_present === 'yes') return true;

  const explicit = booleanFrom(
    firstPresent(
      entryAccessDetails.value.ownerPresent,
      entryAccessDetails.value.owner_present,
      visitAttrs.value.ownerPresent,
      visitAttrs.value.owner_present
    )
  );

  return explicit === true;
});
const shouldShowOwnerFeedback = computed(
  () => isForSale.value && anyOwnerPresent.value
);
const shouldShowOwnerFeedbackAlert = computed(
  () => shouldShowOwnerFeedback.value && finalStatus.value === 'nao_compareceu'
);
const shouldShowForSaleNoEntryAlert = computed(
  () => isForSale.value && form.did_enter === 'no'
);
const steps = computed(() => [
  ...BASE_STEPS,
  ...(shouldShowOwnerFeedback.value
    ? [{ key: 'feedback', label: 'Mensagem ao proprietário' }]
    : []),
]);
const activeStep = computed(
  () => steps.value[stepIndex.value] || steps.value[0]
);
const finishTitle = computed(() =>
  visitFinishTitle({ status: finalStatus.value, t })
);
const visitImovelId = computed(
  () => visit.value.imovel?.id || visit.value.imovelId || visit.value.imovel_id
);
const propertyMissingInfo = computed(() => ({
  has_balcony: form.has_balcony,
  has_gym: form.has_gym,
  has_elevator: form.has_elevator,
  has_pool: form.has_pool,
  has_doorman: form.has_doorman,
}));
const propertyAmenityItems = () => [
  { key: 'has_balcony', label: 'Varanda', value: form.has_balcony },
  { key: 'has_gym', label: 'Academia', value: form.has_gym },
  { key: 'has_pool', label: 'Piscina', value: form.has_pool },
  { key: 'has_doorman', label: 'Portaria', value: form.has_doorman },
];
const propertyInstallationItems = () => [
  { key: 'has_elevator', label: 'Elevador', value: form.has_elevator },
];
const normalizedEntryInfoConfirmed = computed(() => {
  if (form.did_enter === 'no') return 'no';

  if (form.did_enter === 'yes') {
    if (form.correct_entry_info === 'all_correct') return 'yes';
    if (form.correct_entry_info) return 'no';
  }

  return form.entry_info_confirmed || '';
});
const requiresEntranceConfirmation = computed(
  () =>
    form.did_enter === 'yes' &&
    Boolean(form.correct_entry_info) &&
    !['all_correct', 'unknown'].includes(form.correct_entry_info)
);
const requiresEntranceComment = computed(
  () => form.correct_entry_info === 'other' || form.entrance_blocker === 'other'
);
const derivedEntranceProblem = computed(() => {
  if (form.agent_present === 'no') return 'landlord_no_show';
  if (form.visitor_present === 'no') return 'no_show';
  if (form.did_enter !== 'no') return '';
  if (form.entrance_blocker === 'key_problem') {
    return (
      ENTRANCE_PROBLEM_BY_KEY_PROBLEM[form.key_problem] || 'keys_not_available'
    );
  }
  if (form.entrance_blocker === 'wrong_info') return 'missplaced_keys';
  return ENTRANCE_PROBLEM_BY_BLOCKER[form.entrance_blocker] || '';
});
const entryInfoConfirmationValue = value => {
  if (value === 'all_correct') return 'yes';
  if (value) return 'no';
  return '';
};
const referenceAnswerList = value => (value ? [value] : []);
const referenceFollowUpPayload = () =>
  formatReferenceFollowUpPayload({
    agentHasKeys: agentHasKeys.value,
    houseId: visitImovelId.value,
    visitor: {
      id: visit.value.contact?.id || visit.value.contactId,
      schedulingId: visit.value.schedulingId || visit.value.scheduling_id,
    },
    comment: form.owner_feedback || form.comment || null,
    visitType: form.visit_type,
    businessContext: businessContext.value,
    agentHasAttended: form.agent_present === 'yes',
    visitorHasAttended: form.visitor_present === 'yes',
    ownerPresence: referenceAnswerList(form.owner_present),
    keyProblem: form.key_problem || null,
    entranceBlocker: form.entrance_blocker || null,
    entryInfoConfirmation: referenceAnswerList(
      entryInfoConfirmationValue(form.correct_entry_info)
    ),
    correctEntryInfo: form.correct_entry_info || null,
    entranceConfirmation: form.entrance_confirmation || null,
    didEnter: referenceAnswerList(form.did_enter),
    accessType: {
      ownerPresent: shouldAskOwnerPresent.value,
      authorization: entryAccessType.value || null,
    },
  });
const referenceMissingInfoPayload = () =>
  formatReferenceMissingInfoPayload({
    hasBalcony: form.has_balcony,
    hasGym: form.has_gym,
    hasPool: form.has_pool,
    hasElevator: form.has_elevator,
    hasDoorman: form.has_doorman,
  });
const postVisitReportPayload = () => {
  const referenceFollowUp = referenceFollowUpPayload();
  return buildPostVisitReportPayload({
    artifactIdempotencyKey: reportRevisionId.value,
    origin: 'visita_finish',
    status: finalStatus.value,
    amenities: propertyAmenityItems(),
    installations: propertyInstallationItems(),
    followUp: referenceFollowUp,
    photos: [],
    reason: derivedEntranceProblem.value || finalStatus.value,
    reviewContext: {
      requested_outcome: outcome.value,
      final_status: finalStatus.value,
      generated_at: new Date().toISOString(),
      visita_id: route.params.id,
    },
    listingReport: {
      feedback: form.owner_feedback || form.comment || null,
      owner_feedback: form.owner_feedback || null,
      comment: form.comment || null,
      owner_present: form.owner_present || null,
      visit_type: form.visit_type,
      business_context: businessContext.value,
      access_authorization_type: entryAccessType.value || null,
      agent_has_keys: agentHasKeys.value,
      owner_has_attended: anyOwnerPresent.value,
      entry_info_confirmed: normalizedEntryInfoConfirmed.value,
      entrance_confirmation: form.entrance_confirmation || null,
      entrance_blocker: form.entrance_blocker || null,
      correct_entry_info: form.correct_entry_info || null,
      key_problem: form.key_problem || null,
      entrance_problem: derivedEntranceProblem.value || null,
      problem_reason: derivedEntranceProblem.value || null,
      problem: derivedEntranceProblem.value
        ? { reason: derivedEntranceProblem.value }
        : null,
      did_enter: form.did_enter,
      visitor_present: form.visitor_present,
      agent_present: form.agent_present,
      reference_follow_up: referenceFollowUp,
      reference_follow_up_v3:
        formatReferenceFollowUpV3Payload(referenceFollowUp),
      reference_missing_info: referenceMissingInfoPayload(),
    },
  });
};

const finishVisitPayload = () => {
  const ownerPresentAttributes =
    shouldAskOwnerPresent.value || form.owner_present
      ? { owner_present: form.owner_present || null }
      : {};
  const additionalAttributes = {
    ...(visit.value.additionalAttributes ||
      visit.value.additional_attributes ||
      {}),
    ...ownerPresentAttributes,
    visit_followup: {
      ...form,
      entry_info_confirmed: normalizedEntryInfoConfirmed.value,
      entrance_problem: derivedEntranceProblem.value || null,
      status: finalStatus.value,
      finished_at: new Date().toISOString(),
    },
  };

  return {
    status: finalStatus.value,
    visit_type: form.visit_type,
    entrance_problem: derivedEntranceProblem.value || null,
    feedback: form.owner_feedback || form.comment,
    owner_feedback: form.owner_feedback || null,
    additional_attributes: additionalAttributes,
  };
};
const nextVisits = computed(() => {
  const contactId = visit.value.contact?.id || visit.value.contactId;
  if (!contactId) return [];

  return (store.getVisitasList || [])
    .filter(item => {
      const scheduledAt = item.scheduledAt || item.scheduled_at;
      return (
        Number(item.id) !== Number(route.params.id) &&
        Number(item.contact?.id || item.contactId || item.contact_id) ===
          Number(contactId) &&
        ['solicitada', 'agendada', 'confirmada', 'paralisada'].includes(
          item.status
        ) &&
        scheduledAt &&
        new Date(scheduledAt).getTime() >= Date.now()
      );
    })
    .sort(
      (a, b) =>
        new Date(a.scheduledAt || a.scheduled_at).getTime() -
        new Date(b.scheduledAt || b.scheduled_at).getTime()
    )
    .slice(0, 3);
});

const yesNoComplete = (...values) =>
  values.every(value => ['yes', 'no'].includes(value));
const canContinue = computed(() => {
  if (activeStep.value.key === 'presence') {
    if (!yesNoComplete(form.agent_present, form.visitor_present)) return false;
    if (shouldAskOwnerPresent.value && !yesNoComplete(form.owner_present)) {
      return false;
    }
    if (form.agent_present === 'yes' && form.visitor_present === 'yes') {
      return !!form.visit_type;
    }
    return true;
  }

  if (activeStep.value.key === 'entrance') {
    if (!yesNoComplete(form.did_enter)) return false;
    if (form.did_enter === 'yes') {
      if (!form.correct_entry_info) return false;
      if (
        requiresEntranceConfirmation.value &&
        !form.entrance_confirmation.trim()
      ) {
        return false;
      }
      if (requiresEntranceComment.value && !form.comment.trim()) return false;
      return true;
    }

    if (!form.entrance_blocker) return false;
    if (form.entrance_blocker === 'key_problem' && !form.key_problem) {
      return false;
    }
    if (requiresEntranceComment.value && !form.comment.trim()) return false;
    return true;
  }

  if (activeStep.value.key === 'property') {
    return [
      form.has_balcony,
      form.has_gym,
      form.has_elevator,
      form.has_pool,
      form.has_doorman,
    ].every(Boolean);
  }

  return (
    !shouldShowOwnerFeedback.value ||
    finalStatus.value === 'nao_compareceu' ||
    form.owner_feedback.trim().length > 0
  );
});

const loadVisit = async () => {
  isLoading.value = true;
  try {
    visit.value = camelcaseKeys(await store.show(route.params.id), {
      deep: true,
    });
  } finally {
    isLoading.value = false;
  }
};

async function finishVisit() {
  saveError.value = '';
  isSaving.value = true;
  try {
    if (visitImovelId.value) {
      const existingImovelAttrs =
        visit.value.imovel?.additionalAttributes ||
        visit.value.imovel?.additional_attributes ||
        {};

      await ImovelAPI.update(visitImovelId.value, {
        additional_attributes: {
          ...existingImovelAttrs,
          visit_missing_info: {
            ...propertyMissingInfo.value,
            updated_from_visit_id: route.params.id,
            updated_at: new Date().toISOString(),
          },
        },
      });
    }

    const finishedVisit = await store.finish(route.params.id, {
      review_report: postVisitReportPayload(),
      visita: finishVisitPayload(),
    });
    visit.value = finishedVisit;
    isFinished.value = true;
    await router.push(
      frontendURL(
        `accounts/${accountId.value}/visitas/${route.params.id}/sucesso?context=finish`
      )
    );
  } catch {
    saveError.value =
      'Nao foi possivel salvar o resultado da visita. Revise os dados e tente novamente.';
  } finally {
    isSaving.value = false;
  }
}

const nextStep = () => {
  if (!canContinue.value || isSaving.value) return;
  if (stepIndex.value < steps.value.length - 1) {
    stepIndex.value += 1;
    return;
  }
  finishVisit();
};

const previousStep = () => {
  if (stepIndex.value > 0) stepIndex.value -= 1;
};

const close = () =>
  router.push(frontendURL(`accounts/${accountId.value}/visitas`));

const openVisitedHouseLink = () => {
  if (!visitedHouseLink.value || typeof window === 'undefined') return;
  window.open(visitedHouseLink.value, '_blank', 'noopener');
};

watch(
  () => steps.value.length,
  length => {
    if (stepIndex.value >= length) stepIndex.value = Math.max(length - 1, 0);
  }
);

watch(
  () => shouldAskOwnerPresent.value,
  shouldAsk => {
    if (!shouldAsk) form.owner_present = '';
  }
);

watch(
  () => form.did_enter,
  didEnter => {
    if (didEnter === 'yes') {
      form.entrance_blocker = '';
      form.key_problem = '';
      return;
    }

    if (didEnter === 'no') {
      form.correct_entry_info = '';
      form.entrance_confirmation = '';
      form.entry_info_confirmed = 'no';
      return;
    }

    form.entry_info_confirmed = '';
    form.correct_entry_info = '';
    form.entrance_confirmation = '';
    form.entrance_blocker = '';
    form.key_problem = '';
  }
);

watch(
  () => form.correct_entry_info,
  value => {
    if (form.did_enter !== 'yes') return;

    form.entry_info_confirmed = entryInfoConfirmationValue(value);
    if (!requiresEntranceConfirmation.value) form.entrance_confirmation = '';
  }
);

watch(
  () => form.entrance_blocker,
  value => {
    if (value !== 'key_problem') form.key_problem = '';
  }
);

onMounted(loadVisit);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-lais-bg-page font-redHatText md:overflow-hidden"
  >
    <LaisHeader
      title="Finalizar visita"
      helper="Registre presença, entrada no imóvel e próximos passos do pós-visita."
      sticky
      :breadcrumb="[
        { label: 'Visitas', to: { name: 'visitas_index' } },
        { label: 'Finalizar' },
      ]"
    />

    <div
      class="min-h-0 flex-1 overflow-auto px-4 py-8 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:px-6 sm:pb-8 lg:px-10 max-md:flex-none max-md:overflow-visible"
    >
      <VisitFinishSkeleton v-if="isLoading" />

      <div v-else-if="isFinished" class="mx-auto grid max-w-3xl gap-5">
        <LaisCard>
          <div class="grid gap-4 text-center">
            <span
              class="mx-auto grid size-14 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
            >
              <span class="i-lucide-check size-7" aria-hidden="true" />
            </span>
            <h1
              class="font-redHatDisplay text-[28px] font-semibold text-lais-text"
            >
              {{ finishTitle }}
            </h1>
            <p class="text-[14px] text-lais-text-muted">
              O resultado foi salvo no histórico da visita.
            </p>
          </div>
        </LaisCard>
        <div
          v-if="shouldShowForSaleNoEntryAlert"
          class="flex items-start gap-3 rounded-lais border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900"
        >
          <span
            class="i-lucide-alert-triangle mt-0.5 size-5 shrink-0"
            aria-hidden="true"
          />
          <p>
            Visita de venda finalizada sem entrada no imóvel. Registre o motivo
            com clareza e acione o proprietário para reagendar ou corrigir o
            acesso.
          </p>
        </div>
        <LaisCard title="Resumo da visita">
          <div class="grid gap-3">
            <LaisInfoRow
              v-for="row in summaryRows"
              :key="row.label"
              :icon="row.icon"
              :label="row.label"
              :value="row.value"
            />
          </div>
        </LaisCard>
        <LaisCard title="Imóvel visitado">
          <div class="grid gap-3">
            <LaisInfoRow
              icon="i-lucide-building-2"
              label="Anúncio"
              :value="visitedImovel.titulo || visitedImovel.endereco || '—'"
            />
            <LaisInfoRow
              icon="i-lucide-map-pin"
              label="Endereço"
              :value="visitedImovel.endereco || '—'"
            />
            <LaisInfoRow
              icon="i-lucide-badge-dollar-sign"
              label="Valor"
              :value="
                visitedImovel.valorVenda ||
                visitedImovel.valor_venda ||
                visitedImovel.valorAluguel ||
                visitedImovel.valor_aluguel ||
                '—'
              "
            />
            <div v-if="visitedHouseLink" class="flex justify-end">
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-share-2"
                @click="openVisitedHouseLink"
              >
                Compartilhar link
              </LaisPrimaryButton>
            </div>
          </div>
        </LaisCard>
        <LaisCard
          v-if="nextVisits.length"
          :title="`Próximas visitas com ${visit.contact?.name || 'o cliente'}`"
        >
          <div class="grid gap-2">
            <div
              v-for="nextVisit in nextVisits"
              :key="nextVisit.id"
              class="rounded-lais-sm border border-lais-border bg-white px-4 py-3 text-[13px] text-lais-text-body"
            >
              <p class="font-semibold text-lais-text">
                {{ nextVisit.imovel?.titulo || 'Imóvel' }}
              </p>
              <p class="mt-1 text-lais-text-muted">
                {{ formatVisitDateTime(nextVisit.scheduledAt) }}
              </p>
            </div>
          </div>
        </LaisCard>
      </div>

      <div v-else class="mx-auto grid max-w-4xl gap-5">
        <div
          v-if="saveError"
          class="flex items-start gap-3 rounded-lais border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] leading-relaxed text-rose-900"
          data-test="visita-finish-error"
          role="alert"
        >
          <span
            class="i-lucide-alert-circle mt-0.5 size-5 shrink-0"
            aria-hidden="true"
          />
          <p>{{ saveError }}</p>
        </div>

        <div class="flex flex-wrap gap-2">
          <span
            v-for="(step, index) in steps"
            :key="step.key"
            class="rounded-full border px-3 py-1 text-[12px] font-semibold"
            :class="
              index === stepIndex
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : 'border-lais-border text-lais-text-muted'
            "
          >
            {{ index + 1 }}. {{ step.label }}
          </span>
        </div>

        <LaisCard v-if="activeStep.key === 'presence'" title="Quem compareceu?">
          <div class="grid gap-4">
            <fieldset class="grid gap-2">
              <legend class="text-[13px] font-semibold text-lais-text">
                Corretor
              </legend>
              <div
                class="grid gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label="Corretor"
              >
                <button
                  v-for="option in YES_NO_OPTIONS"
                  :key="`agent-${option.value}`"
                  type="button"
                  role="radio"
                  :aria-checked="form.agent_present === option.value"
                  class="min-h-10 rounded-lais border px-3 py-2 text-left text-[13px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
                  :class="radioOptionClass(form.agent_present, option.value)"
                  :data-test="`visita-finish-agent-${option.value}`"
                  @click="form.agent_present = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </fieldset>

            <fieldset class="grid gap-2">
              <legend class="text-[13px] font-semibold text-lais-text">
                Visitante
              </legend>
              <div
                class="grid gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label="Visitante"
              >
                <button
                  v-for="option in YES_NO_OPTIONS"
                  :key="`visitor-${option.value}`"
                  type="button"
                  role="radio"
                  :aria-checked="form.visitor_present === option.value"
                  class="min-h-10 rounded-lais border px-3 py-2 text-left text-[13px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
                  :class="radioOptionClass(form.visitor_present, option.value)"
                  :data-test="`visita-finish-visitor-${option.value}`"
                  @click="form.visitor_present = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </fieldset>

            <fieldset v-if="shouldAskOwnerPresent" class="grid gap-2">
              <legend class="text-[13px] font-semibold text-lais-text">
                Proprietário
              </legend>
              <p class="text-[12px] leading-relaxed text-lais-text-muted">
                Use quando o acesso depende do proprietário ou responsável abrir
                o imóvel.
              </p>
              <div
                class="grid gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label="Proprietário"
              >
                <button
                  v-for="option in YES_NO_OPTIONS"
                  :key="`owner-${option.value}`"
                  type="button"
                  role="radio"
                  :aria-checked="form.owner_present === option.value"
                  class="min-h-10 rounded-lais border px-3 py-2 text-left text-[13px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
                  :class="radioOptionClass(form.owner_present, option.value)"
                  :data-test="`visita-finish-owner-${option.value}`"
                  @click="form.owner_present = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </fieldset>

            <fieldset
              v-if="
                form.agent_present === 'yes' && form.visitor_present === 'yes'
              "
              class="grid gap-2"
            >
              <legend class="text-[13px] font-semibold text-lais-text">
                Como foi realizada a visita?
              </legend>
              <div
                class="grid gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label="Como foi realizada a visita?"
              >
                <button
                  v-for="option in VISIT_TYPE_OPTIONS"
                  :key="`type-${option.value}`"
                  type="button"
                  role="radio"
                  :aria-checked="form.visit_type === option.value"
                  class="min-h-10 rounded-lais border px-3 py-2 text-left text-[13px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
                  :class="radioOptionClass(form.visit_type, option.value)"
                  :data-test="`visita-finish-type-${option.value}`"
                  @click="form.visit_type = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </fieldset>
          </div>
        </LaisCard>

        <LaisCard
          v-else-if="activeStep.key === 'entrance'"
          title="Como foi sua entrada no imóvel?"
        >
          <div class="grid gap-4">
            <fieldset class="grid gap-2">
              <legend class="text-[13px] font-semibold text-lais-text">
                Conseguiu entrar?
              </legend>
              <div
                class="grid gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label="Conseguiu entrar?"
              >
                <button
                  v-for="option in YES_NO_OPTIONS"
                  :key="`did-enter-${option.value}`"
                  type="button"
                  role="radio"
                  :aria-checked="form.did_enter === option.value"
                  class="min-h-10 rounded-lais border px-3 py-2 text-left text-[13px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
                  :class="radioOptionClass(form.did_enter, option.value)"
                  :data-test="`visita-finish-did-enter-${option.value}`"
                  @click="form.did_enter = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </fieldset>

            <div
              v-if="shouldShowForSaleNoEntryAlert"
              class="flex items-start gap-3 rounded-lais border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900"
            >
              <span
                class="i-lucide-alert-triangle mt-0.5 size-5 shrink-0"
                aria-hidden="true"
              />
              <p>
                Venda sem entrada precisa de motivo claro e próximo passo:
                confirme se o acesso deve ser corrigido ou se a visita será
                reagendada com o proprietário.
              </p>
            </div>

            <fieldset v-if="form.did_enter === 'yes'" class="grid gap-2">
              <legend class="text-[13px] font-semibold text-lais-text">
                A informação de entrada estava correta?
              </legend>
              <div
                class="grid gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label="A informação de entrada estava correta?"
              >
                <button
                  v-for="option in CORRECT_ENTRY_INFO_OPTIONS"
                  :key="`correct-entry-${option.value}`"
                  type="button"
                  role="radio"
                  :aria-checked="form.correct_entry_info === option.value"
                  class="min-h-10 rounded-lais border px-3 py-2 text-left text-[13px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
                  :class="
                    radioOptionClass(form.correct_entry_info, option.value)
                  "
                  :data-test="`visita-finish-correct-entry-${option.value}`"
                  @click="form.correct_entry_info = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </fieldset>

            <label v-if="requiresEntranceConfirmation" class="grid gap-1">
              <span class="text-[13px] font-semibold text-lais-text">
                Qual informação correta foi confirmada?
              </span>
              <textarea
                v-model="form.entrance_confirmation"
                rows="3"
                :class="input"
                maxlength="255"
                placeholder="Ex.: senha correta, responsável correto ou instrução atualizada"
              />
            </label>

            <fieldset v-if="form.did_enter === 'no'" class="grid gap-2">
              <legend class="text-[13px] font-semibold text-lais-text">
                O que impediu sua entrada no imóvel?
              </legend>
              <div
                class="grid gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label="O que impediu sua entrada no imóvel?"
              >
                <button
                  v-for="option in ENTRANCE_BLOCKER_OPTIONS"
                  :key="`blocker-${option.value}`"
                  type="button"
                  role="radio"
                  :aria-checked="form.entrance_blocker === option.value"
                  class="min-h-10 rounded-lais border px-3 py-2 text-left text-[13px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
                  :class="radioOptionClass(form.entrance_blocker, option.value)"
                  :data-test="`visita-finish-entrance-blocker-${option.value}`"
                  @click="form.entrance_blocker = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </fieldset>

            <fieldset
              v-if="form.entrance_blocker === 'key_problem'"
              class="grid gap-2"
            >
              <legend class="text-[13px] font-semibold text-lais-text">
                Selecione o problema com a chave
              </legend>
              <div
                class="grid gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label="Selecione o problema com a chave"
              >
                <button
                  v-for="option in KEY_PROBLEM_OPTIONS"
                  :key="`key-problem-${option.value}`"
                  type="button"
                  role="radio"
                  :aria-checked="form.key_problem === option.value"
                  class="min-h-10 rounded-lais border px-3 py-2 text-left text-[13px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
                  :class="radioOptionClass(form.key_problem, option.value)"
                  :data-test="`visita-finish-key-problem-${option.value}`"
                  @click="form.key_problem = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </fieldset>

            <textarea
              v-model="form.comment"
              rows="3"
              :class="input"
              maxlength="255"
              placeholder="Comentário operacional, quando necessário"
            />
            <p class="text-right text-[12px] text-lais-text-muted">
              {{ form.comment.length }}/255
            </p>
          </div>
        </LaisCard>

        <LaisCard
          v-else-if="activeStep.key === 'property'"
          title="Ajude a completar o anúncio"
        >
          <p class="mb-4 text-[13px] leading-relaxed text-lais-text-muted">
            Anúncios completos geram mais visitas
          </p>
          <div class="grid gap-4 sm:grid-cols-2">
            <fieldset
              v-for="field in PROPERTY_FIELDS"
              :key="field.key"
              class="grid gap-2"
            >
              <legend class="text-[13px] font-semibold text-lais-text">
                {{ field.label }}
              </legend>
              <div
                class="grid gap-2"
                role="radiogroup"
                :aria-label="field.label"
              >
                <button
                  v-for="option in propertyOptionsFor(field)"
                  :key="`${field.key}-${option.value}`"
                  type="button"
                  role="radio"
                  :aria-checked="form[field.key] === option.value"
                  class="min-h-10 rounded-lais border px-3 py-2 text-left text-[13px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-lais-primary/20"
                  :class="radioOptionClass(form[field.key], option.value)"
                  :data-test="`visita-finish-property-${field.key}-${option.value}`"
                  @click="form[field.key] = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </fieldset>
          </div>
        </LaisCard>

        <LaisCard
          v-else-if="activeStep.key === 'feedback'"
          title="Mensagem ao proprietário"
        >
          <div class="grid gap-3">
            <div
              v-if="shouldShowOwnerFeedbackAlert"
              class="flex items-start gap-3 rounded-lais border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900"
            >
              <span
                class="i-lucide-alert-triangle mt-0.5 size-5 shrink-0"
                aria-hidden="true"
              />
              <p>
                Compartilhe um resumo objetivo com o proprietário, explicando o
                bloqueio da visita e o próximo passo operacional.
              </p>
            </div>
            <div class="grid gap-1">
              <p class="text-[13px] leading-relaxed text-lais-text-muted">
                Nos ajude a informá-lo como o imóvel está sendo visto.
              </p>
              <p class="text-[13px] font-semibold text-lais-text">
                O que o visitante achou do imóvel?
              </p>
            </div>
            <div
              class="rounded-lais border border-lais-primary/20 bg-lais-primary-50 px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
            >
              <strong class="block text-lais-text">
                Enviaremos ao proprietário!
              </strong>
              Por isso, não informe nome, telefone e endereço; sejam eles seus,
              de clientes ou de outros proprietários.
            </div>
            <textarea
              v-model="form.owner_feedback"
              rows="5"
              :class="input"
              maxlength="650"
              placeholder="Descreva pontos positivos e negativos que chamaram a atenção do cliente."
            />
            <p class="text-right text-[12px] text-lais-text-muted">
              {{ form.owner_feedback.length }}/650
            </p>
          </div>
        </LaisCard>
      </div>
    </div>

    <LaisActionBar sticky-mobile>
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          :disabled="stepIndex === 0 || isFinished"
          @click="previousStep"
        >
          Voltar
        </LaisPrimaryButton>
        <LaisPrimaryButton v-if="isFinished" icon="i-lucide-x" @click="close">
          Fechar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-else
          :is-loading="isSaving"
          :disabled="!canContinue || isSaving"
          icon="i-lucide-arrow-right"
          data-test="visita-finish-next"
          @click="nextStep"
        >
          {{
            stepIndex === steps.length - 1 ? 'Finalizar visita' : 'Continuar'
          }}
        </LaisPrimaryButton>
      </template>
    </LaisActionBar>
  </section>
</template>
